import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import type { RootState } from '../store';
import {
    addMessage,
    updateMessage,
    updateMessageMetadataById,
    removeMessages,
    initialiseMessageThread,
    setIsLoading,
    setIsLoadingChatHistory,
    setError,
    setGateOutcome,
    setConversationId,
    resetGlobalChat,
    resetGateState,
    setConversations,
    setLoadingConversations,
    setAskBack,
    setReasoning,
    resetReasoning,
    setCurrentCompany,
    setIsClearingChat,
    setHasMoreData,
    setPendingNewConversation,
    switchConversation,
} from './actions';
import { getConversationHistory, getUserConversations, streamGlobalAskAicaChat, GateAction, GateReply } from './apiHelper';
import { GLOBAL_HISTORY_PAGE_SIZE, DRAFT_CONVERSATION_KEY, RESUME_HISTORY_RETRY_DELAY_MS } from './constants';
import { GLOBAL_CHAT_CONFIG } from './service';
import { GateOutcome, GlobalMessage, GATE_OUTCOME_TYPE, ASK_BACK_KIND } from './types';
import { ambiguousSlots, buildGateReply, normaliseAskBackSlots, PickedSlot } from './askBackSlots';
import { clearMemory, openReattachStream, stopAskAicaRun, ReattachFrame } from '../dashboard/askAica/apiHelper';
import {
    CLARIFICATION_CANCEL_REASON,
    CLARIFICATION_REPLY_QUERY_SOURCE,
    CLARIFICATION_STALE_ERROR,
    isInProgressStatus,
    MESSAGE_STATUS,
    STOP_SETTLE_FALLBACK_MS,
} from '../dashboard/askAica/constants';
import {
    deriveNewestTurn,
    findClarificationMessage,
    getPendingClarification,
    hasPendingClarification,
} from '../dashboard/askAica/selectors';
import {
    applyClarificationResolution,
    applyOptimisticClarificationAnswers,
    buildClarificationAnswerSummary,
    settleClarificationAsExpired,
} from '../dashboard/askAica/clarificationRound';
import { ClarificationAnswer, ClarificationCard, ClarificationResponse } from '../dashboard/askAica/clarificationTypes';
import { ClaimedAttachment } from '../dashboard/askAica/types';
import { toRowWrittenUpload } from '../dashboard/askAica/attachmentChipState';

// Same short-window turn history the leads chat sends (see askAica/useAskAica.ts) —
// Athena carries the rest of the context server-side via conversation_id.
const HISTORY_TURNS = 3;

/**
 * Maps a conversation-history page onto the portfolio thread. Module-level and
 * pure so both readers of that response — the panel-open load and the degraded
 * fallback — build an identical thread from it.
 *
 * The result is chronological ASCENDING (oldest first), which is what runTurn's
 * messages.slice(-HISTORY_TURNS) window depends on.
 */
const mapHistoryToMessages = (items: any[]): GlobalMessage[] => {
    const messageThread: GlobalMessage[] = [];
    items.forEach((item: { user_message?: any; assistant_message?: any }) => {
        // Company-ID gate ✕-clear marker (query_source='id_clear'): a control row the
        // backend persists ONLY so its sticky current-company lookup knows the company
        // was cleared — it is not a chat turn (no assistant reply) and must never render.
        // Suppress the whole pair on reload (the DB row stays intact; this is render-only).
        // Portfolio-only: in-lead never emits id_clear. Filtering on query_source (not on
        // empty content) also catches legacy clear rows that carry a stale replayed body.
        if (item.user_message?.query_source === 'id_clear') return;
        if (item.user_message) {
            messageThread.push({
                id: item.user_message.id ?? Date.now(),
                type: 'user',
                content: item.user_message.input_text ?? '',
                timestamp: new Date(item.user_message.created_at ?? Date.now()),
                isClarificationAnswer: item.user_message.query_source === CLARIFICATION_REPLY_QUERY_SOURCE,
                // The files the user attached on this turn (read-only chips on the bubble)
                metadata: item.user_message.metadata,
            });
        }
        if (item.assistant_message) {
            messageThread.push({
                id: item.assistant_message.id ?? Date.now() + 1,
                type: 'assistant',
                content: item.assistant_message.input_text ?? '',
                timestamp: new Date(item.assistant_message.created_at ?? Date.now()),
                // Carried so an in-progress turn renders its working state and can be
                // re-attached to after a reload (RECUR-28136).
                status: item.assistant_message.status,
                // The full metadata, so a paused/settled round (and citations, file
                // cards) render after a reload exactly as they do on leads.
                metadata: item.assistant_message.metadata,
                // Athena H-11: structural gate ask-back-prompt flag — see
                // GlobalMessage.is_gate_control doc comment. Additive/
                // optional; undefined when the BE hasn't sent it.
                is_gate_control: item.assistant_message.is_gate_control,
            });
        }
    });
    // Order-agnostic w.r.t. server sort — chronological ascending either way.
    return messageThread.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
};

/**
 * Whether this turn is an ask-back that renders as the docked CompanyPicker, and
 * therefore has no streamed bubble of its own. Deliberately narrower than
 * "is an ask_back": a kind:"clarify" ask-back is prose in the transcript, so it
 * must NOT be suppressed (see the call sites in runTurn).
 */
const isPickerTurn = (gateOutcome?: GateOutcome): boolean =>
    gateOutcome?.type === GATE_OUTCOME_TYPE.ASK_BACK && gateOutcome.ask_back?.kind === ASK_BACK_KIND.PICK;

export const useGlobalAskAica = () => {
    const dispatch = useAppDispatch();
    // useStore() resolves the store from Provider context at render time, unlike the
    // old module-level import (which pulled in every root reducer and broke tests
    // importing this hook). Live read still required for stopRun below.
    const store = useStore<RootState>();
    const userId = useAppSelector((state) => state.user.userData.userId);
    // The caller's own PNO — used ONLY as auth/ownership scoping on the history
    // lookup below (Athena requires it as a query param). This is NOT the
    // resolved-chat-turn company and must never be threaded into runTurn/orgId.
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const {
        messages,
        isLoading,
        isLoadingChatHistory,
        error,
        currentCompany,
        companyLess,
        askBack,
        decline,
        conversationId,
        conversations,
        isLoadingConversations,
        selectedSources,
        reasoning,
        isClearingChat,
        hasMoreData,
        pendingNewConversation,
        background,
    } = useAppSelector((state) => state.globalAskAica);

    // One abort fn PER CONVERSATION. A turn must keep streaming when the user navigates
    // away, so aborting is always addressed at a specific conversation rather than
    // "whatever is in flight".
    const abortsRef = useRef<Map<string, () => void>>(new Map());
    const abortConversation = useCallback((key: string) => {
        const abort = abortsRef.current.get(key);
        if (!abort) return;
        abort();
        abortsRef.current.delete(key);
    }, []);

    // Mirrors leads' streamStartedCallbackRef (useAskAica.ts) — lets the shared
    // LeftPanel know when real answer content has started streaming, so it can
    // hide the transient inline "Thinking…" block (RECUR-27369 F-19).
    const streamStartedCallbackRef = useRef<((v: boolean) => void) | null>(null);

    // F-13 (lost-question race): openChat fires an async conversation-list fetch on
    // modal-open; its onSuccess auto-resumes the most-recent conversation. But the
    // guard there closes over the conversationId/messages/isLoading captured when
    // openChat was BUILT — if the user submits a turn before the list returns, that
    // stale closure still sees "no active chat" and loadConversation clobbers the
    // just-typed question with a prior conversation. This ref always holds the LIVE
    // values (assigned every render, never stale), so the resume guard reads truth.
    const liveStateRef = useRef({ conversationId, messagesLen: messages.length, isLoading, pendingNewConversation, background });
    // Synced in useLayoutEffect (commit phase), never during render — React can
    // replay or discard a render, and a ref write during render would let
    // openChat's async guard read a value from a render that never committed.
    useLayoutEffect(() => {
        liveStateRef.current = { conversationId, messagesLen: messages.length, isLoading, pendingNewConversation, background };
    }, [conversationId, messages.length, isLoading, pendingNewConversation, background]);

    // Monotonic token guarding loadConversation's async history fetch — bumped by
    // runTurn/newChat/loadConversation itself so a SUPERSEDED history response
    // (the user started a turn, reset the chat, or picked another conversation
    // before the fetch returned) is discarded instead of clobbering newer state.
    const historyLoadTokenRef = useRef(0);

    const fetchUserConversations = useCallback(() => {
        if (!userId) return;
        dispatch(setLoadingConversations(true));
        getUserConversations(userId, {
            onSuccess: (data) => dispatch(setConversations(data?.conversations ?? [])),
            // 401 (no principal) or any other failure → empty sidebar, never a crash.
            onError: () => dispatch(setConversations([])),
        });
    }, [dispatch, userId]);

    /** Rewrites a round's card in place. The card lives on an OLDER message than the turn
     * being streamed, in the visible thread or in `conversationKey`'s background buffer. */
    const patchClarificationCard = useCallback(
        (toolUseId: string, conversationKey: string, transform: (card: ClarificationCard) => ClarificationCard) => {
            const state = store.getState().globalAskAica;
            const isActiveThread = conversationKey === (state.conversationId ?? DRAFT_CONVERSATION_KEY);
            const thread = isActiveThread ? state : state.background[conversationKey];
            const message = findClarificationMessage(thread?.messages ?? [], toolUseId);
            const card = message?.metadata?.clarification;
            if (!message || !card) return;
            const next = transform(card);
            if (next !== card) dispatch(updateMessageMetadataById(message.id, { clarification: next }, conversationKey));
        },
        [dispatch, store],
    );

    const runTurn = useCallback(
        (options: {
            displayContent?: string;
            apiContent?: string;
            gateReply?: GateReply;
            gateAction?: GateAction;
            clarificationResponse?: ClarificationResponse;
            attachmentDocIds?: string[];
            pendingUploads?: ClaimedAttachment[];
        }) => {
            if (isLoading) return;
            // A paused round blocks every non-answer send; the server's new_turn cancel is only the backstop.
            if (!options.clarificationResponse && hasPendingClarification(messages[messages.length - 1])) return;

            console.info('[AskAica:portfolio] turn start', {
                conversationId,
                sources: selectedSources,
                hasApiContent: !!options.apiContent,
                displayContentLength: options.displayContent?.length ?? 0,
                gateAction: options.gateAction ? Object.keys(options.gateAction)[0] : undefined,
                gateReply: options.gateReply ? Object.keys(options.gateReply)[0] : undefined,
            });

            // A new turn supersedes any pending history load (e.g. resume-on-open
            // still in flight when the user starts typing) — bump the token so
            // that load's result is discarded rather than overwriting this turn.
            historyLoadTokenRef.current += 1;

            // This turn belongs to the conversation it STARTED in, even if the user
            // navigates away mid-stream. A New Chat has no server id yet, so it begins on
            // the draft key and is re-keyed the moment conversation_id lands.
            const turnKey = { current: conversationId ?? DRAFT_CONVERSATION_KEY };
            const isTurnActive = () => (liveStateRef.current.conversationId ?? DRAFT_CONVERSATION_KEY) === turnKey.current;
            const rekeyTurn = (realId: string) => {
                if (turnKey.current === realId) return;
                const abort = abortsRef.current.get(turnKey.current);
                abortsRef.current.delete(turnKey.current);
                if (abort) abortsRef.current.set(realId, abort);
                turnKey.current = realId;
            };

            // Abort only THIS conversation's previous stream — a turn running in another
            // conversation must be left alone.
            abortConversation(turnKey.current);

            const userMessageId = Date.now();
            if (options.displayContent || options.attachmentDocIds?.length) {
                dispatch(
                    addMessage(
                        {
                            id: userMessageId,
                            type: 'user',
                            content: options.displayContent ?? '',
                            timestamp: new Date(),
                            isClarificationAnswer: !!options.clarificationResponse,
                            // Optimistic sent cards: the final frame REPLACES this array. Conditional
                            // so a turn with no attachments keeps metadata absent, not {}.
                            ...(options.pendingUploads?.length ? { metadata: { uploaded_attachments: options.pendingUploads } } : {}),
                        },
                        turnKey.current,
                    ),
                );
            }

            const aiMessageId = Date.now() + 1;

            // The server answered "already answered / no such round": undo the optimistic
            // pair and expire the card, unless the live resolution already settled it.
            const rollBackStaleAnswer = () => {
                dispatch(removeMessages([userMessageId, aiMessageId], turnKey.current));
                if (options.clarificationResponse) {
                    patchClarificationCard(options.clarificationResponse.tool_use_id, turnKey.current, (card) =>
                        settleClarificationAsExpired(card, CLARIFICATION_CANCEL_REASON.NEW_TURN),
                    );
                }
            };
            // The user's own picks are known now: show the Q&A note immediately instead of waiting on the server.
            if (options.clarificationResponse) {
                const { tool_use_id, answers } = options.clarificationResponse;
                patchClarificationCard(tool_use_id, turnKey.current, (card) => applyOptimisticClarificationAnswers(card, answers));
            }
            dispatch(addMessage({ id: aiMessageId, type: 'assistant', content: '', timestamp: new Date() }, turnKey.current));
            dispatch(setIsLoading(true, turnKey.current));
            dispatch(setError(null));
            dispatch(resetReasoning());
            streamStartedCallbackRef.current?.(false);

            const historyTurns = messages.slice(-HISTORY_TURNS).map((msg) => ({
                role: msg.type === 'user' ? 'user' : 'assistant',
                content: msg.content,
            }));
            // gate_action-only turns (switch/clear) don't add a new utterance —
            // they replay the existing transcript with the gate delta attached.
            const apiMessages =
                options.apiContent !== undefined
                    ? [...historyTurns, { role: 'user', content: options.apiContent }].slice(-HISTORY_TURNS)
                    : historyTurns;

            try {
                const abortStream = streamGlobalAskAicaChat(
                    {
                        messages: apiMessages,
                        attachmentDocIds: options.attachmentDocIds,
                        userId,
                        // INVARIANT: the global surface NEVER sends body org_id / the
                        // x-organization-id header, on ANY turn — including follow-ups
                        // after a company has already been resolved. Pinning the org here
                        // would let Athena treat the turn as company-scoped and skip the
                        // gate, which is exactly what breaks in-thread switching (naming a
                        // new company mid-conversation no longer triggers ask_back/switch).
                        // The gate alone drives which company is active each turn via its
                        // resolved/continue/switch/ask_back outcome; continuity comes from
                        // conversation_id, not from the FE echoing currentCompany.org_id.
                        orgId: null,
                        conversationId,
                        gateReply: options.gateReply,
                        gateAction: options.gateAction,
                        clarificationResponse: options.clarificationResponse,
                        // D1/D2: never send the flag together with an explicit id.
                        newConversation: pendingNewConversation && !conversationId ? true : undefined,
                        // Parked on the message (not a ref) so Stop can still address
                        // the run after this surface has unmounted and taken its local
                        // abort handle with it (RECUR-28136 D6).
                        // The parameter is named apart from the outer optimistic `userMessageId`
                        // above: the second dispatch must address the optimistic bubble, not the
                        // server's id.
                        onUserMessageId: (serverUserMessageId) => {
                            dispatch(
                                updateMessage(aiMessageId, undefined, undefined, undefined, turnKey.current, {
                                    userMessageId: serverUserMessageId,
                                }),
                            );
                            // This frame proves Athena committed the attachment rows, so the sent
                            // cards open before the answer lands (one-shot: apiHelper.ts guards it).
                            if (options.pendingUploads?.length) {
                                dispatch(
                                    updateMessageMetadataById(
                                        userMessageId,
                                        { uploaded_attachments: options.pendingUploads.map(toRowWrittenUpload) },
                                        turnKey.current,
                                    ),
                                );
                            }
                        },
                    },
                    (
                        content,
                        gateOutcome,
                        chunkConversationId,
                        reasoning,
                        hasNewReasoning,
                        sources,
                        answerScope,
                        clarification,
                        clarificationResolution,
                    ) => {
                        // Evaluated up front: rekeyTurn below moves turnKey to the real id
                        // while the active conversation is still null for one render, so a
                        // later isTurnActive() call in this same callback would read false.
                        const active = isTurnActive();
                        // A PICK ask-back's raw prompt ("I found multiple companies matching…")
                        // is never persisted as message content — the docked CompanyPicker IS
                        // the whole response for that turn (see AskAicaInputBox's portfolio-only
                        // picker). Skipping it here means there is nothing to suppress at
                        // render time, and the message stays empty even once it's no longer
                        // the last one in the thread.
                        //
                        // 🔴 Scoped to kind:"pick" — NOT to every ask_back. A kind:"clarify"
                        // ask-back ("What does 'dashboard' refer to?") has no card: its prompt
                        // IS the turn's only content, it is answered by typing a normal
                        // composer message, and suppressing it would hide the sole thing the
                        // turn says (ruled by the architecture session 2026-08-23).
                        const isPickAskBack = isPickerTurn(gateOutcome);
                        if (!isPickAskBack) {
                            dispatch(
                                updateMessage(
                                    aiMessageId,
                                    content,
                                    sources,
                                    answerScope,
                                    turnKey.current,
                                    clarification ? { clarification } : undefined,
                                ),
                            );
                        }
                        // A resume's final frame settles the card on the OLDER paused message.
                        if (clarificationResolution) {
                            patchClarificationCard(clarificationResolution.tool_use_id, turnKey.current, (card) =>
                                applyClarificationResolution(card, clarificationResolution),
                            );
                        }
                        // Real answer content (a pick turn keeps the picker up instead of a
                        // streamed bubble) — flip the signal so LeftPanel hides the transient
                        // inline "Thinking…" block (leads parity, see useAskAica.ts:229).
                        if (!isPickAskBack && content && active) streamStartedCallbackRef.current?.(true);
                        if (chunkConversationId) {
                            rekeyTurn(chunkConversationId);
                            if (active) dispatch(setConversationId(chunkConversationId));
                        }
                        // Gate/company state is single-slot, not per-conversation — a
                        // background turn must never re-assert the chip for the chat you are
                        // currently reading.
                        if (gateOutcome && active) dispatch(setGateOutcome(gateOutcome));
                        dispatch(setReasoning(reasoning, hasNewReasoning, turnKey.current));
                    },
                    (response) => {
                        const active = isTurnActive();
                        console.info('[AskAica:portfolio] turn end', {
                            conversationId: response.conversationId ?? conversationId,
                            status: 'success',
                        });
                        dispatch(setIsLoading(false, turnKey.current));
                        if (response.clarificationStale) {
                            abortsRef.current.delete(turnKey.current);
                            rollBackStaleAnswer();
                            return;
                        }
                        // The frame is a conversation/org-wide status broadcast; this bubble owns only the ids this turn sent.
                        const sentDocIds = new Set(options.attachmentDocIds ?? []);
                        const ownUploads = response.uploadedAttachments?.filter((a) => sentDocIds.has(a.mnemosyne_doc_id));
                        if (ownUploads?.length) {
                            dispatch(updateMessageMetadataById(userMessageId, { uploaded_attachments: ownUploads }, turnKey.current));
                        }
                        if (response.content && !isPickerTurn(response.gateOutcome)) {
                            dispatch(
                                updateMessage(aiMessageId, response.content, response.sources, response.answerScope, turnKey.current, {
                                    status: response.status,
                                }),
                            );
                        } else if (response.status) {
                            // RECUR-28136: a Stop mid-reasoning (no answer text yet) must
                            // still settle the message's status so "Generation stopped"
                            // can render — leave content untouched rather than blank it.
                            dispatch(
                                updateMessage(aiMessageId, undefined, undefined, undefined, turnKey.current, { status: response.status }),
                            );
                        }
                        if (response.conversationId) {
                            rekeyTurn(response.conversationId);
                            if (active) dispatch(setConversationId(response.conversationId));
                        }
                        if (response.gateOutcome && active) dispatch(setGateOutcome(response.gateOutcome));
                        abortsRef.current.delete(turnKey.current);
                        // Each SSE frame already dispatched its own reasoning delta via onChunk
                        // above — re-dispatching the accumulated response.reasoning here would
                        // append it a second time (setReasoning APPENDS, see reducer.ts).
                        // Refresh the sidebar so a new conversation appears without waiting for a poll.
                        fetchUserConversations();
                    },
                    (err) => {
                        // Enriched with backend correlation (conversationId/status) so a
                        // failed turn can be tied back to Athena's logs. Deliberately
                        // excludes responseSnippet — it's raw Athena response body text
                        // (up to 500 chars) that can carry business-sensitive content, and
                        // has no place in a browser console / log aggregator even though
                        // err is a plain Error here (see apiHelper.ts), not an AxiosError
                        // carrying a Cerberus token.
                        console.error('Error in streamGlobalAskAicaChat:', {
                            conversationId,
                            status: err?.status,
                            message: err?.message,
                        });
                        dispatch(setIsLoading(false, turnKey.current));
                        abortsRef.current.delete(turnKey.current);
                        if (err?.code === CLARIFICATION_STALE_ERROR) {
                            rollBackStaleAnswer();
                            return;
                        }
                        // A background turn's failure must not reset or error the chat the
                        // user is currently reading.
                        if (!isTurnActive()) return;
                        // A-29: a held conversation_id was deleted elsewhere — drop it,
                        // refresh the sidebar, and land in a fresh New Chat instead of
                        // surfacing the raw "HTTP error! Status: 404".
                        if (err?.status === 404) {
                            dispatch(resetGlobalChat());
                            dispatch(setPendingNewConversation(true));
                            fetchUserConversations();
                            dispatch(setError('This conversation is no longer available. Starting a new chat.'));
                            return;
                        }
                        dispatch(setError(err?.message || 'Failed to get response'));
                        // No conversationId to re-dispatch here: onChunk above already
                        // dispatches setConversationId as soon as any chunk carries one,
                        // independent of this turn's outcome — a mid-stream error can't
                        // lose an id that already landed in Redux (F-18).
                    },
                );
                abortsRef.current.set(turnKey.current, abortStream);
            } catch (err: any) {
                dispatch(setIsLoading(false, turnKey.current));
                dispatch(setError(err?.message || 'Failed to send message'));
            }

            return () => abortConversation(turnKey.current);
        },
        [
            dispatch,
            isLoading,
            messages,
            selectedSources,
            userId,
            currentCompany,
            conversationId,
            fetchUserConversations,
            pendingNewConversation,
            patchClarificationCard,
        ],
    );

    /** A normal typed message — also how an ask_back free-text reply ("2", "yes",
     * a name/identifier) is sent: the gate interprets it, no gate_reply attached.
     * Returns runTurn's abort cleanup so the shared AskAicaInputBox's Stop-chat
     * button works on the portfolio surface too (RECUR-27369). */
    const sendMessage = useCallback(
        (content: string, attachmentDocIds?: string[], pendingUploads?: ClaimedAttachment[]) => {
            // Leads-parity send-guard (F-18): blocks while a turn is already in
            // flight, so a message typed before the previous turn's
            // conversation_id has arrived can't fire a second /chat call with no
            // conversation_id and fragment into a brand-new conversation.
            if ((!content.trim() && !attachmentDocIds?.length) || isLoading) return undefined;
            return runTurn({ displayContent: content, apiContent: content, attachmentDocIds, pendingUploads });
        },
        [runTurn, isLoading],
    );

    /** Answers the pending round: ONE send carrying every question's pick/typed text. */
    const answerClarification = useCallback(
        (card: ClarificationCard, answers: ClarificationAnswer[]) => {
            const summary = buildClarificationAnswerSummary(card, answers);
            return runTurn({
                displayContent: summary,
                apiContent: summary,
                clarificationResponse: { tool_use_id: card.tool_use_id, answers },
            });
        },
        [runTurn],
    );

    /**
     * Confirming the picker form. ONE turn carrying EVERY ambiguous slot's choice —
     * atomic by contract (v4 §5): a partial submission is rejected server-side and
     * the form re-prompted, so there is no such thing as answering one slot now and
     * another later. This replaces the old submit-on-row-tap behaviour.
     *
     * The reply's SHAPE is decided by buildGateReply, which also keeps sending the
     * legacy `selected_org_id` for a single-slot ask so this build can safely reach
     * production before a RECUR-27888 Athena does.
     */
    const replyToGate = useCallback(
        (picked: PickedSlot[]) => {
            if (!picked.length) return;
            const pick = askBack?.kind === ASK_BACK_KIND.PICK ? askBack : null;
            const { isLegacyOnly, slots } = normaliseAskBackSlots(pick);
            const reply = buildGateReply(
                picked.map(({ slot_id, candidate }) => ({ slot_id, selected_org_id: candidate.org_id })),
                { isLegacyOnly, ambiguousCount: ambiguousSlots(slots).length },
            );
            // What the user sees themselves having said. Joined with the same
            // separator the answer-scope line uses, so a multi-company turn reads
            // consistently on the way out and on the way back.
            const label = picked.map(({ candidate }) => candidate.name).join(' · ');
            runTurn({ displayContent: label, apiContent: label, gateReply: reply });
        },
        [runTurn, askBack],
    );

    /**
     * Tapping a company chip on an answer's scope line — pins that company for the
     * next turn. Sends `gate_action: {switch: org_id}` with NO message. Mirrors
     * clearCompany's message-less gate_action turn.
     *
     * 🔴 The only call site today renders from `answer_scope.covered`, so in
     * practice only an org_id the backend itself just named gets sent. That is a
     * property of the WIRING, not a check: the org is **not** validated
     * client-side, and this function accepts any non-empty string. Athena's PNO
     * revalidation in `_handle_gate_action` is the control — a raw client org_id
     * is an IDOR surface, and that server-side check is the only thing standing
     * on it. Do NOT read the sentence above as an allowlist; if you add a second
     * call site (a sidebar, a recent-companies list, a deep link), you are
     * sending an unvalidated org_id into the switch path and there is no
     * frontend guard behind you.
     */
    const pinCompany = useCallback(
        (orgId: string) => {
            if (!orgId) return;
            runTurn({ gateAction: { switch: orgId } });
        },
        [runTurn],
    );

    /** Picker's "✕" — dismisses the open ask_back picker without picking a candidate. */
    const dismissAskBack = useCallback(() => dispatch(setAskBack(null)), [dispatch]);

    /** Chip "✕" — clears the resolved company, back to company-less.
     * Switching is still done implicitly by naming a new company in the next
     * message, which the gate resolves on its own. The one EXPLICIT switcher is
     * pinCompany above (RECUR-27889's answer-scope chips), which is what finally
     * uses GateAction.switch. */
    const clearCompany = useCallback(() => {
        runTurn({ gateAction: { clear: true } });
    }, [runTurn]);

    /** New Chat — drops the held conversation_id + gate/message state and arms
     * the next send to force-create a conversation (D1/D2). Dispatched AFTER
     * resetGlobalChat — order matters, see reducer.ts's comment on resetGlobalChat. */
    const newChat = useCallback(() => {
        // Resetting supersedes any pending history load — discard its result.
        historyLoadTokenRef.current += 1;
        // A conversation with a real id is PARKED — its turn keeps streaming into its own
        // buffer so returning to it shows the finished answer. A draft cannot be parked
        // (no row to return to, and its key would collide with the new chat's), so its
        // stream is aborted instead — otherwise its chunks would land in the new thread.
        if (!conversationId) abortConversation(DRAFT_CONVERSATION_KEY);
        dispatch(switchConversation(null));
        dispatch(resetGlobalChat());
        dispatch(setPendingNewConversation(true));
    }, [dispatch, conversationId, abortConversation]);

    /** D3 per-row delete: branches on whether the deleted id is the currently
     * ACTIVE conversation. ACTIVE aborts any in-flight stream (mirrors the old
     * clearChat's guard, SF-4) so a late chunk from the deleted conversation
     * can't re-assert its conversation_id/company after deletion, then resets to
     * a fresh New Chat. BACKGROUND must NOT abort — the active conversation may
     * legitimately still be streaming — and leaves messages/conversationId/chip
     * completely untouched. */
    const deleteConversation = useCallback(
        (targetConversationId: string) => {
            const isActive = targetConversationId === conversationId;
            abortConversation(targetConversationId);
            if (isActive) abortConversation(DRAFT_CONVERSATION_KEY);
            dispatch(setIsClearingChat(true));
            clearMemory(userId, isActive ? currentCompany?.org_id : undefined, targetConversationId)
                .then(() => {
                    // Per-row delete — a background delete never clears the active
                    // chat, so "chat memory cleared" overstates what happened.
                    toast.success('Conversation deleted');
                    // Optimistic row removal (D3(iii)) — the DB soft-deletes are already
                    // synchronous by the time this 200 lands, so the fetchUserConversations()
                    // refetch below is a correctness refresh, not the source of truth for
                    // removing the row. Mirrors the leads deleteConversation (useAskAica.ts).
                    dispatch(setConversations(conversations.filter((c) => c.conversation_id !== targetConversationId)));
                    if (isActive) newChat();
                    fetchUserConversations();
                })
                .catch((err) => {
                    // Never log the raw error — an AxiosError's `config.headers` carries the
                    // session cerberus token (see useAskAicaFileActions.ts::openFile).
                    console.error('Error clearing chat:', err instanceof Error ? err.message : 'unknown error');
                    toast.error('Failed to delete conversation');
                    fetchUserConversations();
                })
                .finally(() => dispatch(setIsClearingChat(false)));
        },
        [conversationId, userId, currentCompany, conversations, newChat, fetchUserConversations, dispatch],
    );

    /** "Delete chat" header entry point — delegates to deleteConversation's
     * ACTIVE branch, keyed on conversation_id (RECUR-27369 parity with the
     * leads clearChatMemory). No-op when there's nothing to clear (no active
     * conversation). Isolated to the global slice — never touches the leads
     * askAica reducer. */
    const clearChat = useCallback(() => {
        if (!conversationId) return;
        deleteConversation(conversationId);
    }, [conversationId, deleteConversation]);

    // See useAskAica.ts for what this flag means; the two surfaces follow the
    // same re-attach policy through their own dispatch shapes.
    const [reattachExhausted, setReattachExhausted] = useState(false);

    /**
     * The degraded fallback (D16): ONE history read when the re-attach buffer is
     * gone. A total replace is correct HERE and only here — the portfolio surface
     * has no load-more, so refetching the same GLOBAL_HISTORY_PAGE_SIZE window
     * restores byte-identical content and there is no scrollback to lose. The
     * moment portfolio gains pagination this must adopt the leads merge rule.
     *
     * Deliberately NOT loadConversation: its `parked?.isLoading` short-circuit
     * would skip exactly the in-progress case this exists for.
     */
    const syncGlobalThread = useCallback(
        (convId: string) => {
            getConversationHistory(convId, userId, investorOrgId)
                .then((data) => {
                    if (convId !== liveStateRef.current.conversationId) return;
                    dispatch(initialiseMessageThread(mapHistoryToMessages(Array.isArray(data?.items) ? data.items : [])));
                    dispatch(setCurrentCompany(data.current_company ?? null));
                })
                .catch((err) => {
                    // Never log the raw error — an AxiosError's `config.headers` carries the
                    // session cerberus token (see useAskAicaFileActions.ts::openFile).
                    console.error('Error refetching global conversation history:', err instanceof Error ? err.message : 'unknown error');
                });
        },
        [dispatch, userId, investorOrgId],
    );

    /** Follow a turn that is still running server-side — the reloaded-page case.
     * Reuses the shared re-attach client (the same module this slice already takes
     * parseSSELines from) rather than re-implementing the transport. */
    const startReattach = useCallback(
        (userMessageId: string, assistantMessageId: number | string, convId: string) => {
            const opened = openReattachStream(userMessageId, {
                // Org-less by invariant — see globalAskAica/service.ts.
                headers: GLOBAL_CHAT_CONFIG.getHeaders(null, userId),
                onFrame: (frame: ReattachFrame) => {
                    // The buffer replay stamps the settled terminal status onto its final
                    // frame's metadata — prefer that over the is_final hardcode so a
                    // stopped turn re-attaches as 'partial', not 'complete' (RECUR-28136).
                    const status = frame.is_final
                        ? (frame.metadata?.status as string | undefined) ?? MESSAGE_STATUS.COMPLETE
                        : MESSAGE_STATUS.RUNNING;
                    dispatch(
                        updateMessage(
                            assistantMessageId,
                            frame.message?.content ?? '',
                            frame.metadata?.sources,
                            frame.metadata?.answer_scope,
                            convId,
                            { status, clarification: frame.metadata?.clarification },
                        ),
                    );
                    const resolution = frame.metadata?.clarification_resolution;
                    if (resolution) {
                        patchClarificationCard(resolution.tool_use_id, convId, (card) => applyClarificationResolution(card, resolution));
                    }
                    if (frame.reasoning) dispatch(setReasoning(frame.reasoning, !!frame.has_new_reasoning, convId));
                },
                onDegraded: () => syncGlobalThread(convId),
                onEnd: (outcome) => {
                    if (outcome === 'fatal') {
                        dispatch(
                            updateMessage(assistantMessageId, undefined, undefined, undefined, convId, { status: MESSAGE_STATUS.COMPLETE }),
                        );
                        return;
                    }
                    if (outcome === 'exhausted') {
                        syncGlobalThread(convId);
                        setReattachExhausted(true);
                    }
                },
            });
            if (opened) setReattachExhausted(false);
        },
        [dispatch, userId, syncGlobalThread, patchClarificationCard],
    );

    /** Loads a conversation's message pairs — today, only ever the user's single
     * active portfolio conversation (single-conversation-per-user model,
     * 2026-08-10), called from openChat's resume-on-open. No user-facing
     * switcher exists, so there's no "conversation A already streaming, switch
     * to B" case to guard against here (see the unmount-cleanup abort in the
     * effect above for the general "surface goes away mid-stream" case).
     * The history response carries the conversation's last resolved company
     * (current_company: {org_id, name} | null — Athena BE follow-up for F-6), so the
     * "Answering for X" chip IS restored on reload (see the setCurrentCompany dispatch
     * below); a company-less conversation returns null and the chip stays clear.
     * Resets gate state first (currentCompany/companyLess/askBack/decline, same reset
     * newChat performs) so a stale chip can never leak into the next turn — the chip
     * is re-established solely from this conversation's current_company. */
    const loadConversation = useCallback(
        (convId: string, isResumeRetry = false) => {
            // A resume-retry re-reads the SAME already-active conversation (the
            // setTimeout guard below only ever schedules one for convId ===
            // liveStateRef.current.conversationId) — none of the one-time
            // switch/reset dispatches below apply to it, so skip straight to the
            // re-fetch rather than re-running them for no behavioural change.
            if (!isResumeRetry) {
                // Parks the outgoing turn (if still streaming) and restores this one's buffer.
                // Read through the live ref: this callback's deps do not include `background`,
                // so the closure copy can be stale and would let the fetch below overwrite a
                // still-streaming thread.
                const parked = liveStateRef.current.background[convId];
                // A draft is never parked (see reducer::switchConversation), so its stream would
                // keep running with nowhere visible to land — abort it on the way out, matching
                // newChat.
                if (!liveStateRef.current.conversationId) abortConversation(DRAFT_CONVERSATION_KEY);
                dispatch(switchConversation(convId));
                // Skip the history fetch ONLY while the restored turn is still streaming, where
                // fetching would overwrite a live thread. A turn that finished — or FAILED, whose
                // error was suppressed because it was not the active conversation — leaves a
                // buffer that may be partial, so let the server reconcile it.
                if (parked?.isLoading) {
                    dispatch(resetGateState());
                    return;
                }
                // SF-3: clear pendingNewConversation SYNCHRONOUSLY here, not only via
                // the SET_CONVERSATION_ID dispatch at the end of a successful load —
                // that dispatch never fires on the catch path (:416+), and a
                // superseded load returns early on the token check above, so New
                // Chat → pick a row → type would otherwise still see the flag set
                // and force-create a THIRD conversation instead of continuing the
                // picked one.
                dispatch(setPendingNewConversation(false));
                dispatch(setIsLoadingChatHistory(true));
                dispatch(resetGateState());
            }
            // Bumping invalidates any still-pending PRIOR load (rapid double-open,
            // a switch mid-fetch, or the read this retry is superseding) — its
            // .then/.catch below will see a stale token and discard its result
            // instead of overwriting whatever this call (or a later turn/reset)
            // produces.
            const token = ++historyLoadTokenRef.current;
            getConversationHistory(convId, userId, investorOrgId)
                .then((data) => {
                    // Superseded by a new turn, newChat, or another loadConversation
                    // call while this fetch was in flight — discard, don't clobber.
                    if (historyLoadTokenRef.current !== token) return;
                    const items = Array.isArray(data?.items) ? data.items : [];
                    // F-9: a full page hints older messages may exist — real signal
                    // instead of a hardcoded false (the portfolio surface has no
                    // "load more" UI yet, so this is informational for now).
                    dispatch(setHasMoreData(items.length >= GLOBAL_HISTORY_PAGE_SIZE));
                    const messageThread = mapHistoryToMessages(items);
                    dispatch(initialiseMessageThread(messageThread));
                    dispatch(setConversationId(convId));
                    // F-6: restore the "Answering for <company>" chip. Athena's history
                    // response now carries the conversation's last resolved company
                    // (current_company: {org_id, name} | null). null → company-less
                    // conversation, chip stays clear. The resetGateState() above cleared
                    // any prior chip before this load, so this is the sole source of truth.
                    dispatch(setCurrentCompany(data.current_company ?? null));

                    // A turn still running server-side. Portfolio's thread is
                    // OLDEST-first, so the newest turn is the tail pair. `!isLoading`
                    // is the no-double-stream guard — a panel reopened over a still-live
                    // stream must never open a second one for the same run.
                    const newestTurn = deriveNewestTurn(messageThread[messageThread.length - 1], messageThread[messageThread.length - 2]);
                    if (
                        newestTurn.isInProgress &&
                        newestTurn.userMessageId &&
                        newestTurn.assistantMessageId !== undefined &&
                        !liveStateRef.current.isLoading
                    ) {
                        startReattach(newestTurn.userMessageId, newestTurn.assistantMessageId, convId);
                    } else if (!isResumeRetry && messageThread[messageThread.length - 1]?.type === 'user') {
                        // RECUR-28136 regression 2: a turn started an instant before this
                        // read landed has no assistant row yet, so deriveNewestTurn sees a
                        // lone trailing user message and reports "not in progress" — this
                        // resume-on-open read only ever runs once, so without a re-check
                        // the thinking state never appears until the panel is reopened.
                        // One bounded retry, and only if nothing else has since taken over
                        // this conversation (a live send, or a switch away).
                        setTimeout(() => {
                            const live = liveStateRef.current;
                            if (live.isLoading || live.conversationId !== convId) return;
                            loadConversation(convId, true);
                        }, RESUME_HISTORY_RETRY_DELAY_MS);
                    }
                })
                .catch((err) => {
                    if (historyLoadTokenRef.current !== token) return;
                    // Never log the raw error — an AxiosError's `config.headers` carries the
                    // session cerberus token (see useAskAicaFileActions.ts::openFile).
                    console.error('Error fetching global conversation history:', err instanceof Error ? err.message : 'unknown error');
                    dispatch(setIsLoadingChatHistory(false));
                    dispatch(setError('Failed to load conversation'));
                });
        },
        [dispatch, userId, investorOrgId, abortConversation, startReattach],
    );

    /** Called when the portfolio surface mounts. Fetches the user's conversation
     * list (GET /chat/conversations/user — under Conversations v2 this can be
     * any number of rows) so the sidebar's "Your Conversations" is populated,
     * without auto-loading any of them into the main view — the orb always
     * opens to a fresh chat (design call: opening the orb should default to
     * New Chat on both desktop and mobile, not silently resume the last
     * conversation). Previously (F-B / D4β) this auto-loaded the most recent
     * conversation on open, mirroring the leads chat's resume-on-open — that
     * behavior is intentionally removed here, not the list fetch itself. */
    const openChat = useCallback(() => {
        // Opening is a New Chat: the global slice survives close, so without this a
        // reopen would show the previous transcript, and a first send with neither a
        // conversation id nor the new-conversation flag could let Athena continue the
        // old thread. newChat parks a real conversation (it stays in the sidebar and
        // keeps streaming into its own buffer) and arms pendingNewConversation.
        // Runs before the userId guard so the reset never depends on identity timing.
        newChat();
        if (!userId) return;
        dispatch(setLoadingConversations(true));
        getUserConversations(userId, {
            onSuccess: (data) => {
                dispatch(setConversations(data?.conversations ?? []));
            },
            onError: () => dispatch(setConversations([])),
        });
    }, [dispatch, userId, newChat]);

    const registerStreamStartedCallback = useCallback((cb: (v: boolean) => void) => {
        streamStartedCallbackRef.current = cb;
    }, []);

    // Portfolio's thread is OLDEST-first (its history mapper sorts ascending and
    // addMessage appends), so the newest turn is the tail pair.
    const newestTurn = deriveNewestTurn(messages[messages.length - 1], messages[messages.length - 2]);
    const pendingClarification = getPendingClarification(messages[messages.length - 1]);

    /** Stop the run server-side. A client disconnect is no longer a cancel, and
     * abortsRef is per-hook-instance — gone after a panel close/reopen or a
     * reload — so this endpoint is what keeps Stop working at all.
     *
     * See useAskAica.ts::stopRun for the full contract: resolves true once the
     * server accepts the stop (caller must leave the stream open for Athena's
     * synthetic final frame) or false if the request itself failed (caller falls
     * back to the local abort). Also arms the same bounded local-settle fallback
     * in case that frame never arrives — reading straight off the Redux store
     * (not liveStateRef) so a real settlement landing after this hook has
     * unmounted is never stomped (see useAskAica.ts::stopRun). Resolves the
     * turn's OWN thread first — top-level state if turnKey is still the active
     * conversation, `background[turnKey]` otherwise — so a conversation switch
     * during the 10s window still finds and settles the right thread instead of
     * reading the now-unrelated top-level isLoading (CodeRabbit finding 2).
     * Gates on the resolved thread's own row status, not `isLoading` — a newer
     * turn started in the same conversation after this one already settled
     * also leaves `isLoading` true, so the abort/setIsLoading(false) side
     * effects additionally require this captured turn to still be the
     * thread's active one (CodeRabbit follow-up finding). */
    const stopRun = useCallback(() => {
        if (!newestTurn.userMessageId) return Promise.resolve(false);
        const assistantMessageId = newestTurn.assistantMessageId;
        const turnKey = conversationId ?? DRAFT_CONVERSATION_KEY;
        return stopAskAicaRun(newestTurn.userMessageId, GLOBAL_CHAT_CONFIG.getHeaders(null, userId))
            .then((stopResult) => {
                // See useAskAica.ts::stopRun — a paused round has no stream to settle it, so the
                // 200 itself settles the card (also covers a second tab / a lost first 200).
                if (pendingClarification && stopResult?.clarification_cancelled) {
                    patchClarificationCard(pendingClarification.tool_use_id, turnKey, (card) =>
                        settleClarificationAsExpired(card, CLARIFICATION_CANCEL_REASON.STOP),
                    );
                } else if (pendingClarification && stopResult?.already_terminal && conversationId) {
                    syncGlobalThread(conversationId);
                }
                if (assistantMessageId !== undefined) {
                    setTimeout(() => {
                        const state = store.getState().globalAskAica;
                        const isActiveThread = turnKey === (state.conversationId ?? DRAFT_CONVERSATION_KEY);
                        const thread = isActiveThread ? state : state.background[turnKey];
                        const row = thread?.messages.find((message: GlobalMessage) => message.id === assistantMessageId);
                        // The row's OWN status is the sole settle gate — `isLoading` can't
                        // tell this turn apart from a NEWER one started in the same
                        // conversation after this one already settled (that newer turn
                        // would also leave isLoading true).
                        if (!isInProgressStatus(row?.status)) return; // already settled
                        // Only abort the live stream / clear isLoading if this captured
                        // turn is still the thread's active one — otherwise a newer turn's
                        // stream would be killed by this stale timer.
                        const isStillActiveTurn =
                            !!thread &&
                            deriveNewestTurn(thread.messages[thread.messages.length - 1], thread.messages[thread.messages.length - 2])
                                .assistantMessageId === assistantMessageId;
                        if (isStillActiveTurn && thread?.isLoading) {
                            abortConversation(turnKey);
                            dispatch(setIsLoading(false, turnKey));
                        }
                        dispatch(
                            updateMessage(assistantMessageId, undefined, undefined, undefined, turnKey, { status: MESSAGE_STATUS.PARTIAL }),
                        );
                    }, STOP_SETTLE_FALLBACK_MS);
                }
                return true;
            })
            .catch(() => false); // already logged in stopAskAicaRun
    }, [
        newestTurn.userMessageId,
        newestTurn.assistantMessageId,
        userId,
        conversationId,
        dispatch,
        abortConversation,
        pendingClarification,
        patchClarificationCard,
        syncGlobalThread,
    ]);

    return {
        messages,
        isLoading,
        isLoadingChatHistory,
        error,
        currentCompany,
        companyLess,
        askBack,
        decline,
        conversationId,
        conversations,
        isLoadingConversations,
        selectedSources,
        sendMessage,
        replyToGate,
        dismissAskBack,
        clearCompany,
        pinCompany,
        newChat,
        clearChat,
        deleteConversation,
        pendingNewConversation,
        loadConversation,
        fetchUserConversations,
        openChat,
        reasoning,
        isClearingChat,
        hasMoreData,
        registerStreamStartedCallback,
        hasInProgressTurn: newestTurn.isInProgress,
        pendingClarification,
        hasPendingClarification: !!pendingClarification,
        answerClarification,
        stopRun,
        reattachExhausted,
    };
};

export default useGlobalAskAica;
